import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  bookingTypeEnumToTranslatedText,
  constructCalendarHighlights,
  constructCalendarReservedDates,
  convertJSDateToDotNetDateTimeDayEnd,
  convertJSDateToDotNetDateTimeDayStart,
  differenceBetweenDatesAsDaysInteger,
} from "../../common/misc";
import { useAuthUser } from "../../features/auth/authHooks";
import Page from "../../types/Page";
import Layout from "../../components/Layout";
import PageHeader from "../../components/PageHeader";
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  Row,
  Spinner,
} from "react-bootstrap";
import { ICalEventModal } from "../../components/ICalEventModal";
import ReactDatePicker from "react-datepicker";
import { Link, useParams } from "react-router-dom";
import {
  BookingItem,
  BookingsRequest,
  getAccommodationsBookingsAsync,
  ReservedDate,
  selectAccommodationsBookingItems,
  selectBookingItemsLoadingStatus,
} from "./bookingsSlice";
import { selectAccommodationsLoadingStatus } from "../AccommodationsPage/accommodationsSlice";
import { LOADING_STATUS } from "../../common/constants";
import { start } from "repl";
import BookingRowsTable from "./BookingRowsTable";
import BookingModalWrapper from "./BookingModalWrapper";
import { DateRange } from "react-day-picker";
import ReactDayPickerWrapper from "../../components/DayPicker/ReactDayPickerWrapper";

const BookingsCabinPageType: Page = {
  ActiveTab: "Bookings",
  DisplayName: "12345 Ankkalinna",
  DisplayParentName: "Varauskalenteri / Kohde",
  LinkUrl: "/bookings",
  Path: "/bookings",
  RouteName: "bookings",
};

const LegendBaseCssClass =
  "body-small d-inline-block position-relative ps-24 mb-8 me-8 text-muted react-datepicker__legend react-datepicker__legend--info";

const BookingsCabinPage = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const { ownerAccountId } = useAuthUser();
  const { isOwnerUser } = useAuthUser();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(null);
  let bookings = useAppSelector((state) =>
    selectAccommodationsBookingItems(state, params.accommodationId),
  );
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [dayPickerSelectedRange, setDayPickerSelectedRange] =
    useState<DateRange>();
  const bookingsLoadingStatus = useAppSelector(selectBookingItemsLoadingStatus);
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const accommodationName = urlParams.get("accommodationName");
  const accommodationId = urlParams.get("accommodationId");
  const onChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };
  const initFetch = useCallback((date: Date) => {
    let bookingsRequest: BookingsRequest = new (class
      implements BookingsRequest
    {
      accommodationId: string;
      ownerId: string;
      isOwnerUser: boolean;
      bookingsStartDate: string;
      bookingsEndDate: string;
    })();
    bookingsRequest.accommodationId = params.accommodationId;
    bookingsRequest.ownerId = ownerAccountId;
    bookingsRequest.isOwnerUser = isOwnerUser;
    /*
    let currentDate = new Date();
    let calendarDate = new Date();
     */
    //Set date to first of the fetched month.
    let startFetchDate = new Date(date);
    startFetchDate.setDate(1);
    let calendarStartFetchDate = new Date(startFetchDate);
    //Set enddate as 1 of the month 4 months from now
    let calendarEndDate = new Date(
      startFetchDate.setMonth(startFetchDate.getMonth() + 1),
    );
    bookingsRequest.bookingsStartDate = convertJSDateToDotNetDateTimeDayStart(
      calendarStartFetchDate,
    );
    bookingsRequest.bookingsEndDate =
      convertJSDateToDotNetDateTimeDayEnd(calendarEndDate);
    dispatch(getAccommodationsBookingsAsync(bookingsRequest));
  }, []);
  useEffect(() => {
    initFetch(new Date());
  }, []);
  const onMonthChange = (date: Date) => {
    console.log("changing month");
    initFetch(date);
  };
  const [showModal, setShowModal] = useState(false);
  const [showICalModal, setShowICalModal] = useState(false);
  const [selectedICalBooking, setSelectedICalBooking] =
    useState<ReservedDate | null>(null);

  const handleIcalClick = (reservedDate: ReservedDate) => {
    setSelectedICalBooking(reservedDate);
    setShowICalModal(true);
  };

  let highlightWithRangesForBookings = [];
  const onDatePickerRangeSelected = (selected: DateRange) => {
    if (selected.from != null && selected.to != null) {
      setDayPickerSelectedRange(selected);
      setBookingModalOpen(true);
    }
    //console.log(triggerDate);
  };

  let reservedDates: { [key: string]: ReservedDate } = {};
  if (bookings != null) {
    highlightWithRangesForBookings = constructCalendarHighlights(bookings);
    reservedDates = constructCalendarReservedDates(bookings);
  }
  return (
    <div className={BookingsCabinPageType.RouteName}>
      <Layout CurrentPage={BookingsCabinPageType}>
        <PageHeader
          Title={accommodationName}
          SubTitle={BookingsCabinPageType.DisplayParentName}
        />

        <div
          className="react-datepicker-inline
        react-datepicker-weeknumbers"
          style={{ maxWidth: 640, margin: "0 auto" }}
        >
          <ReactDayPickerWrapper
            selectionMode="range"
            interval={1}
            reservedDates={reservedDates}
            changeMonth={(date) => onMonthChange(date)}
            numberOfMonths={1}
            accommodationId={params.accommodationId}
            onRangeSelected={onDatePickerRangeSelected}
            allowRangeSelectOnReservedDates={false}
            showResetRangeButton={true}
            noPastDatesSelectable={true}
            onIcalClick={handleIcalClick}
          />
        </div>
        <div>
          <div className="mt-32 mx-16 mb-0 text-center">
            <p className={`${LegendBaseCssClass} react-datepicker__legend--a`}>
              <span className="d-none">A </span>= alustava varaus
            </p>
            <p className={`${LegendBaseCssClass} react-datepicker__legend--i`}>
              <span className="d-none">I </span>= internetvaraus
            </p>
            <p className={`${LegendBaseCssClass} react-datepicker__legend--o`}>
              <span className="d-none">O </span>= omistajan varaus
            </p>
            <p className={`${LegendBaseCssClass} react-datepicker__legend--n`}>
              <span className="d-none">N </span>= normaalivaraus
            </p>
            <p className={`${LegendBaseCssClass} react-datepicker__legend--r`}>
              <span className="d-none">R </span>= ryhmävaraus
            </p>
            <p className={`${LegendBaseCssClass} react-datepicker__legend--v`}>
              <span className="d-none">V </span>= väliaikaisesti pois myynnistä
            </p>
            <p className={`${LegendBaseCssClass} react-datepicker__legend--y`}>
              <span className="d-none">Y </span>= omistajan ilmoittama varaus
            </p>
            <p className={`${LegendBaseCssClass} react-datepicker__legend--iC`}>
              <span className="d-none">iC </span>= iCal -tapahtuma
            </p>
          </div>
          <BookingRowsTable
            bookings={bookings}
            accommodationId={accommodationId}
            clearAllPastBookings={false}
            onICalSaved={() => initFetch(new Date())}
          />
        </div>
        <BookingModalWrapper
          accommodationId={accommodationId}
          accommodationName={accommodationName}
          accommodationNumber={""}
          showModal={bookingModalOpen}
          selectedDateRange={dayPickerSelectedRange}
          setParentShowBookingModal={setBookingModalOpen}
          callParentInitFetch={() =>
            initFetch(new Date(dayPickerSelectedRange.from))
          }
        />

        {/* iCal Blocked Period Modal */}
        <ICalEventModal
          show={showICalModal}
          onHide={() => setShowICalModal(false)}
          eventData={selectedICalBooking}
          accommodationId={accommodationId}
          onSave={() => initFetch(new Date())}
        />
      </Layout>
    </div>
  );
};

export default BookingsCabinPage;
